import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppState } from "../../contexts/App";
import { isApiError } from "../../services/api/errors";
import { getPayments, PaymentsState } from "../../services/payments";

export const prefix = "pages.settings.payments";
export const chargesPrefix = "pages.charges";

/** undefined: loading; "unavailable": the API has payments off; "error": couldn't load */
export type PaymentsLoad = PaymentsState | "unavailable" | "error" | undefined;

/** The other Keepos' names, one without a title named as such. */
export const keepoNames = (sites: { title: string }[], t: TFunction) =>
  sites.map(site => site.title.trim() || t(`${prefix}.untitledKeepo`)).join(", ");

/**
 * The Keepo open now and its payments (registration, settings, fees), loaded once per Keepo. An answer for another
 * Keepo (the person switched meanwhile) is dropped; `isCurrent` tells a later save the same.
 */
export const usePayments = () => {
  const { appState } = useAppState();
  const siteId = appState.editingSiteId as string;
  const [payments, setPayments] = useState<PaymentsLoad>();
  const currentSiteId = useRef(siteId);
  currentSiteId.current = siteId;

  useEffect(() => {
    let isCurrent = true;
    setPayments(undefined);
    getPayments(siteId)
      .then(value => isCurrent && setPayments(value))
      .catch(error => isCurrent && setPayments(isApiError(error, "PAYMENTS_DISABLED") ? "unavailable" : "error"));
    return () => {
      isCurrent = false;
    };
  }, [siteId]);

  const isCurrent = useCallback((site: string) => currentSiteId.current === site, []);

  return { siteId, payments, setPayments, isCurrent };
};
